/**
 * Supplier Dashboard V2 - MaintainX Style with Sidebar
 * Enterprise-grade supplier portal with backorder pipeline integration
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Package,
  Clock,
  CheckCircle2,
  DollarSign,
  Truck,
  FileText,
  RefreshCw,
  LogOut,
  Building2,
  History,
  Eye,
  Loader2,
  Settings,
  Send,
  X,
  Upload,
  Image as ImageIcon,
  Megaphone,
  TrendingUp,
  LayoutGrid,
  Table2,
  Calendar,
  AlertTriangle,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  ClipboardList,
  Plus,
  Award,
  Star,
  ShieldAlert
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import SupplierLayout from "@/components/layouts/SupplierLayout";
import PortalInvoiceSubmit from "@/components/portal/PortalInvoiceSubmit";
import BackorderPipeline from "@/components/BackorderPipeline";
import SupplierBackorderPipeline from "@/components/SupplierBackorderPipeline";
import SupplierCampaignBuilder from "@/components/SupplierCampaignBuilder";
import TelegramConnect from "@/components/TelegramConnect";
import TelegramInviteBanner from "@/components/TelegramInviteBanner";
import SupplierSettingsModal from "@/components/SupplierSettingsModal";
import DashboardHeroBanner, { DashboardSearchBar } from "@/components/ui/DashboardHeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color = "blue" }) => {
  const colorStyles = {
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    green: "bg-emerald-50 text-emerald-600",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default function SupplierDashboardV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "requests";
  
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [stats, setStats] = useState({});
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [backorderViewMode, setBackorderViewMode] = useState("pipeline"); // "pipeline" or "table"
  const [backorderRequests, setBackorderRequests] = useState([]);
  const [loadingBackorders, setLoadingBackorders] = useState(false);
  const [trendingDemand, setTrendingDemand] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [proposalForm, setProposalForm] = useState(null);
  const [proposalItems, setProposalItems] = useState([]);
  const [proposalNotes, setProposalNotes] = useState("");
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    item_id: "",
    item_name: "",
    manufacturer_number: "",
    description: "",
    quantity_available: 1,
    unit_price: 0,
    lead_time_days: 3,
    notes: "",
    original_or_substitute: "original",
    freight_included: true
  });
  
  // Quote management state
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetailModal, setShowQuoteDetailModal] = useState(false);
  const [showEditQuoteModal, setShowEditQuoteModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editQuoteForm, setEditQuoteForm] = useState({});
  const [savingQuote, setSavingQuote] = useState(false);
  const [deletingQuote, setDeletingQuote] = useState(false);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [verification, setVerification] = useState(null);

  const token = localStorage.getItem("supplier_token");

  useEffect(() => {
    if (!token) {
      navigate("/supplier/login");
      return;
    }
    fetchDashboard();
    fetchVerification();
  }, [token, navigate]);

  const fetchVerification = async () => {
    try {
      const res = await fetch(`${API_URL}/api/supplier/me/verification-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setVerification(await res.json());
    } catch (e) { /* ignore */ }
  };

  // Update tab from URL params
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === "proposals") fetchProjectsForProposals();
  }, [activeTab]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/suppliers/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("supplier_token");
          localStorage.removeItem("supplier_info");
          navigate("/supplier/login");
          return;
        }
        throw new Error("Failed to fetch dashboard");
      }
      
      const data = await res.json();
      setSupplier(data.supplier);
      setStats(data.stats);
      setQuoteRequests(data.quote_requests);
      setMyQuotes(data.my_quotes);
      setActivityLog(data.activity_log);
      setDashboardData(data);
      
      // Also fetch backorder requests for table view
      fetchBackorderRequests();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackorderRequests = async () => {
    setLoadingBackorders(true);
    try {
      const [requestsRes, quotesRes] = await Promise.all([
        fetch(`${API_URL}/api/suppliers/quote-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/suppliers/quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        const requests = requestsData.requests || [];
        
        // Get quotes to determine status
        let quotes = [];
        if (quotesRes.ok) {
          const quotesData = await quotesRes.json();
          quotes = quotesData.quotes || [];
        }
        
        // Enrich requests with quote status
        const enrichedRequests = requests.map(req => {
          const myQuote = quotes.find(q => q.backorder_id === req.id);
          return {
            ...req,
            my_quote: myQuote,
            has_quoted: !!myQuote,
            quote_status: myQuote?.status || null
          };
        });
        
        setBackorderRequests(enrichedRequests);
      }
    } catch (err) {
      console.error("Failed to fetch backorder requests:", err);
    } finally {
      setLoadingBackorders(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("supplier_token");
    localStorage.removeItem("supplier_info");
    navigate("/supplier/login");
    toast.success("Logged out successfully");
  };

  const fetchProjectsForProposals = async () => {
    try {
      const supplierInfo = JSON.parse(localStorage.getItem("supplier_info") || "{}");
      const email = supplierInfo.email || supplier?.email || "";
      const res = await fetch(`${API_URL}/api/project-command/proposals/for-supplier?supplier_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setAvailableProjects(data.projects || []);
        setMyProposals(data.my_proposals || []);
      }
    } catch (e) { /* ignore */ }
  };

  const startProposal = (project) => {
    setProposalForm(project);
    setProposalItems([{ phase: "", proposed_cost: 0, included_above: false }]);
    setProposalNotes("");
  };

  const submitProposal = async () => {
    if (!proposalForm) return;
    setSubmittingProposal(true);
    try {
      const supplierInfo = JSON.parse(localStorage.getItem("supplier_info") || "{}");
      const res = await fetch(`${API_URL}/api/project-command/proposals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: proposalForm.id,
          project_name: proposalForm.name,
          supplier_name: supplier?.contact_name || supplierInfo.name || "",
          supplier_email: supplier?.email || supplierInfo.email || "",
          supplier_company: supplier?.company_name || supplier?.name || "",
          cost_line_items: proposalItems,
          notes: proposalNotes
        })
      });
      if (res.ok) {
        toast.success("Proposal submitted successfully!");
        setProposalForm(null);
        setProposalItems([]);
        fetchProjectsForProposals();
      } else {
        toast.error("Failed to submit proposal");
      }
    } catch (e) { toast.error("Error submitting proposal"); }
    setSubmittingProposal(false);
  };

  // Open quote modal with request data
  const openQuoteModal = (request) => {
    setSelectedRequest(request);
    // Pre-fill form with first item from request
    const firstItem = request.items?.[0] || {};
    setQuoteForm({
      item_id: firstItem.id || firstItem.item_id || "",
      item_name: firstItem.name || firstItem.item_name || "",
      manufacturer_number: firstItem.part_number || firstItem.manufacturer_number || "",
      description: firstItem.description || request.notes || "",
      quantity_available: firstItem.quantity || 1,
      unit_price: 0,
      lead_time_days: 3,
      notes: "",
      original_or_substitute: "original",
      freight_included: true
    });
    setShowQuoteModal(true);
  };

  // Submit quote to hospital
  const handleSubmitQuote = async () => {
    if (!selectedRequest) return;
    
    if (!quoteForm.item_name || !quoteForm.unit_price || quoteForm.unit_price <= 0) {
      toast.error("Please fill in item name and price");
      return;
    }
    
    setSubmittingQuote(true);
    try {
      const res = await fetch(`${API_URL}/api/suppliers/direct-quote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          request_id: selectedRequest.id,
          ...quoteForm
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to submit quote");
      }
      
      toast.success(data.message || "Quote submitted successfully!");
      setShowQuoteModal(false);
      setSelectedRequest(null);
      fetchDashboard(); // Refresh dashboard
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmittingQuote(false);
    }
  };

  // View quote details
  const openQuoteDetail = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteDetailModal(true);
  };

  // Open edit modal for a quote
  const openEditQuote = (quote) => {
    setSelectedQuote(quote);
    setEditQuoteForm({
      item_name: quote.item_name || "",
      description: quote.description || "",
      quantity_available: quote.quantity_available || 1,
      unit_price: quote.unit_price || 0,
      lead_time_days: quote.lead_time_days || 3,
      notes: quote.notes || "",
      original_or_substitute: quote.original_or_substitute || "original",
      freight_included: quote.freight_included !== false
    });
    setShowEditQuoteModal(true);
    setShowQuoteDetailModal(false);
  };

  // Save edited quote
  const handleSaveEditedQuote = async () => {
    if (!selectedQuote || !editQuoteForm.unit_price) {
      toast.error("Please enter a valid price");
      return;
    }
    
    setSavingQuote(true);
    try {
      // Transform form data to match backend model (SupplierQuoteItemSubmission)
      const backendPayload = {
        item_id: selectedQuote.item_id || selectedQuote.id,
        item_name: editQuoteForm.item_name,
        original_or_substitute: editQuoteForm.original_or_substitute || "original",
        description: editQuoteForm.notes || editQuoteForm.description,
        quantity_available: editQuoteForm.quantity_available,
        cost: editQuoteForm.unit_price,  // Backend uses 'cost' field
        freight_included: editQuoteForm.freight_included,
        eta_days: editQuoteForm.lead_time_days
      };
      
      // Use direct edit endpoint
      const res = await fetch(`${API_URL}/api/suppliers/quotes/${selectedQuote.id}/edit`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(backendPayload)
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update quote");
      
      toast.success("Quote updated and hospital notified!");
      setShowEditQuoteModal(false);
      setSelectedQuote(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingQuote(false);
    }
  };

  // Delete quote
  const handleDeleteQuote = async () => {
    if (!selectedQuote) return;
    
    setDeletingQuote(true);
    try {
      // Try direct delete endpoint first
      const res = await fetch(`${API_URL}/api/suppliers/quotes/${selectedQuote.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to delete quote");
      
      toast.success("Quote deleted successfully");
      setShowDeleteConfirm(false);
      setShowQuoteDetailModal(false);
      setSelectedQuote(null);
      fetchDashboard();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingQuote(false);
    }
  };

  // Fetch trending demand across hospitals
  const fetchTrendingDemand = async () => {
    try {
      const res = await fetch(`${API_URL}/api/suppliers/trending-demand`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrendingDemand(data.trending_items || []);
      }
    } catch (err) {
      console.error("Failed to fetch trending demand:", err);
    }
  };

  useEffect(() => {
    if (token && supplier) {
      fetchTrendingDemand();
    }
  }, [token, supplier]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Get title based on active tab
  const getPageTitle = () => {
    switch (activeTab) {
      case "requests": return "Quote Requests";
      case "quotes": return "My Quotes";
      case "backorders": return "Backorder Pipeline";
      case "orders": return "Active Orders";
      case "earnings": return "Earnings";
      case "invoices": return "Invoices & Payments";
      case "certification": return "Certification";
      case "activity": return "Activity Log";
      case "settings": return "Settings";
      default: return "Dashboard";
    }
  };

  // Handle sidebar action clicks (like Campaign Builder)
  const handleSidebarAction = (actionId) => {
    if (actionId === "campaign") {
      setShowCampaignBuilder(true);
    }
  };

  return (
    <SupplierLayout
      title={getPageTitle()}
      subtitle={`Welcome back, ${supplier?.contact_name || supplier?.name || "Supplier"}`}
      supplier={supplier}
      onLogout={handleLogout}
      onAction={handleSidebarAction}
      alertCampaignEnabled={supplier?.alert_campaign_enabled ?? false}
      actions={
        <div className="flex items-center gap-2">
          <TelegramConnect token={token} userType="supplier" />
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowSettings(true)}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      }
    >
      {/* Hero Banner */}
      <DashboardHeroBanner 
        portalType="supplier" 
        activeSection={
          activeTab === "requests" ? "quotes" :
          activeTab === "history" ? "orders" :
          activeTab === "pipeline" ? "quickbids" :
          "overview"
        }
      />

      {/* Verification Status Banner */}
      {verification && (
        verification.is_verified ? (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between" data-testid="supplier-verified-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-semibold text-emerald-900">Account Verified</p>
                <p className="text-sm text-emerald-700">You&apos;re fully approved and can be matched on quote requests.</p>
              </div>
            </div>
            {verification.verified_at && (
              <span className="text-xs text-emerald-700 hidden sm:block">Verified {new Date(verification.verified_at).toLocaleDateString()}</span>
            )}
          </div>
        ) : (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="supplier-pending-banner">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900">Verification Pending</p>
                <p className="text-sm text-amber-700 mb-2">
                  {verification.all_uploaded
                    ? "All documents received. Your account is awaiting admin review."
                    : "Upload all required documents below. An administrator will mark you Verified once everything is in place."}
                </p>
                {verification.checklist?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {verification.checklist.map((c) => (
                      c.uploaded ? (
                        <span key={c.type} className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" />
                          {c.name}
                        </span>
                      ) : (
                        <label key={c.type} className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-700 cursor-pointer hover:bg-amber-100" data-testid={`supplier-upload-${c.type}`}>
                          <Clock className="w-3 h-3" />
                          {c.name}
                          <span className="ml-1 text-[10px] underline">+ Upload</span>
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.xls"
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              if (!f) return;
                              const fd = new FormData();
                              fd.append("file", f);
                              fd.append("document_type", c.type);
                              try {
                                const res = await fetch(`${API_URL}/api/supplier/me/documents/upload`, {
                                  method: "POST",
                                  headers: { Authorization: `Bearer ${token}` },
                                  body: fd,
                                });
                                if (res.ok) {
                                  toast.success(`${c.name} uploaded — pending admin review`);
                                  fetchVerification();
                                } else {
                                  toast.error("Upload failed");
                                }
                              } catch (_err) { toast.error("Upload failed"); }
                              e.target.value = "";
                            }}
                          />
                        </label>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}
      
      {/* Search Bar Below Banner */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <DashboardSearchBar 
          placeholder="Search quotes, orders, inventory..."
          className="w-full sm:w-auto"
        />
      </div>
      
      {/* Telegram Invite Banner */}
      <TelegramInviteBanner 
        token={token} 
        userType="supplier" 
        userName={supplier?.contact_name?.split(' ')[0] || supplier?.name?.split(' ')[0]}
      />

      {/* Stats Row - Only show on main dashboard */}
      {activeTab === "requests" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatsCard icon={FileText} label="Quote Requests" value={stats.total_quote_requests || 0} color="blue" />
          <StatsCard icon={Clock} label="Pending Quotes" value={stats.pending_quotes || 0} color="amber" />
          <StatsCard icon={DollarSign} label="Submitted Quotes" value={stats.submitted_quotes || 0} color="purple" />
          <StatsCard icon={CheckCircle2} label="Accepted Quotes" value={stats.won_quotes || 0} color="green" />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === "requests" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Pending Quote Requests</h3>
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {quoteRequests.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-slate-900 font-medium mb-2">No quote requests</h3>
              <p className="text-slate-500">You&apos;ll see new requests here when they&apos;re assigned to you.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quoteRequests.map((req) => (
                <div key={req.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-slate-400 text-sm font-mono">#{req.id.substring(0, 8)}</span>
                      <p className="text-slate-900 font-medium">{req.hospital_name || "Hospital"}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      req.priority === "high" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"
                    }`}>
                      {req.priority?.toUpperCase() || "NORMAL"}
                    </span>
                  </div>
                  
                  <div className="space-y-1 mb-4">
                    {req.items?.slice(0, 2).map((item, idx) => (
                      <p key={idx} className="text-slate-600 text-sm">
                        • {item.item_name || item.name} (Qty: {item.quantity})
                      </p>
                    ))}
                    {req.items?.length > 2 && (
                      <p className="text-slate-400 text-sm">+{req.items.length - 2} more items</p>
                    )}
                  </div>
                  
                  <Button 
                    onClick={() => openQuoteModal(req)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid={`view-submit-quote-${req.id}`}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    View & Submit Quote
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "quotes" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Quote History</h3>
              <p className="text-sm text-slate-500">All quotes you&apos;ve submitted to hospitals</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Item</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Hospital</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Qty</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Your Price</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Lead Time</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Status</th>
                  <th className="text-left p-4 text-slate-600 font-medium text-sm">Submitted</th>
                  <th className="text-right p-4 text-slate-600 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {myQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="font-medium">No quotes submitted yet</p>
                      <p className="text-sm">Submit your first quote from the Quote Requests tab</p>
                    </td>
                  </tr>
                ) : (
                  myQuotes.map((quote) => (
                    <>
                      <tr 
                        key={quote.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setExpandedQuote(expandedQuote === quote.id ? null : quote.id)}
                        data-testid={`quote-row-${quote.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {expandedQuote === quote.id ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                            <div>
                              <p className="text-slate-900 font-medium">{quote.item_name || "Item"}</p>
                              <p className="text-slate-400 text-xs font-mono">#{quote.id?.substring(0, 8)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-slate-600">{quote.request_hospital || "N/A"}</td>
                        <td className="p-4 text-slate-600">{quote.quantity_available || "-"}</td>
                        <td className="p-4 text-emerald-600 font-mono font-semibold">${quote.unit_price?.toFixed(2)}</td>
                        <td className="p-4 text-slate-600">{quote.lead_time_days || 3} days</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            quote.status === "accepted" || quote.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                            quote.status === "rejected" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>
                            {quote.status?.toUpperCase() || "PENDING"}
                          </span>
                        </td>
                        <td className="p-4 text-slate-500 text-sm">
                          {quote.created_at ? new Date(quote.created_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openQuoteDetail(quote)}
                              data-testid={`view-quote-${quote.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {quote.status !== "approved" && quote.status !== "accepted" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditQuote(quote)}
                                  data-testid={`edit-quote-${quote.id}`}
                                >
                                  <Edit className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setSelectedQuote(quote); setShowDeleteConfirm(true); }}
                                  data-testid={`delete-quote-${quote.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedQuote === quote.id && (
                        <tr className="bg-slate-50">
                          <td colSpan={8} className="p-4">
                            <div className="grid md:grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs text-slate-500 uppercase mb-1">Description</p>
                                <p className="text-slate-700 text-sm">{quote.description || "No description"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase mb-1">Type</p>
                                <p className="text-slate-700 text-sm">{quote.original_or_substitute === "original" ? "Original Item" : "Substitute"}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase mb-1">Freight</p>
                                <p className="text-slate-700 text-sm">{quote.freight_included ? "Included" : "Not Included"}</p>
                              </div>
                              {quote.notes && (
                                <div className="md:col-span-3">
                                  <p className="text-xs text-slate-500 uppercase mb-1">Notes</p>
                                  <p className="text-slate-700 text-sm">{quote.notes}</p>
                                </div>
                              )}
                              {quote.revision_history && quote.revision_history.length > 0 && (
                                <div className="md:col-span-3">
                                  <p className="text-xs text-slate-500 uppercase mb-1">Revision History</p>
                                  <div className="space-y-1">
                                    {quote.revision_history.map((rev, idx) => (
                                      <p key={idx} className="text-slate-600 text-xs">
                                        {new Date(rev.timestamp).toLocaleString()}: Price changed from ${rev.old_price?.toFixed(2)} to ${rev.new_price?.toFixed(2)}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "backorders" && (
        <div className="space-y-4">
          {/* Action Buttons Row */}
          <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Backorder Pipeline</h3>
              <p className="text-sm text-slate-500">View and respond to hospital backorder requests</p>
            </div>
            <div className="flex gap-3">
              {/* View Toggle */}
              <div className="flex items-center bg-slate-100 rounded-lg p-1">
                <Button
                  variant={backorderViewMode === "pipeline" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setBackorderViewMode("pipeline")}
                  className={backorderViewMode === "pipeline" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}
                  data-testid="pipeline-view-btn"
                >
                  <LayoutGrid className="w-4 h-4 mr-1" />
                  Pipeline
                </Button>
                <Button
                  variant={backorderViewMode === "table" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setBackorderViewMode("table")}
                  className={backorderViewMode === "table" ? "bg-white shadow-sm text-slate-900" : "text-slate-600"}
                  data-testid="table-view-btn"
                >
                  <Table2 className="w-4 h-4 mr-1" />
                  Table
                </Button>
              </div>
              <Button 
                onClick={() => setShowCampaignBuilder(true)}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="create-alert-btn"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </div>
          </div>

          {/* Trending Demand Section */}
          {trendingDemand.length > 0 && (
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-amber-900">Trending Demand</h4>
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                  {trendingDemand.length} items
                </span>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {trendingDemand.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-amber-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{item.item_name}</p>
                        <p className="text-xs text-slate-500">Part #: {item.part_number || "N/A"}</p>
                      </div>
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                        {item.demand_count} hospitals
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Total qty needed: {item.total_quantity_needed}
                    </p>
                    <Button 
                      size="sm" 
                      className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-xs"
                      onClick={() => {
                        setQuoteForm({
                          ...quoteForm,
                          item_name: item.item_name,
                          manufacturer_number: item.part_number || ""
                        });
                        setShowCampaignBuilder(true);
                      }}
                    >
                      I Have Stock
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pipeline View */}
          {backorderViewMode === "pipeline" && (
            <SupplierBackorderPipeline 
              supplierId={supplier?.id}
              authToken={token}
              onRefresh={fetchDashboard}
              onSubmitQuote={openQuoteModal}
            />
          )}

          {/* Table View */}
          {backorderViewMode === "table" && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">All Backorder Requests</h3>
                <Button variant="ghost" size="sm" onClick={fetchBackorderRequests}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingBackorders ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Request ID</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Hospital</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Item</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Part #</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600 uppercase">Qty</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600 uppercase">Priority</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600 uppercase">My Quote</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                      <th className="text-center p-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingBackorders ? (
                      <tr>
                        <td colSpan="10" className="p-8 text-center">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                          <span className="text-slate-500">Loading requests...</span>
                        </td>
                      </tr>
                    ) : backorderRequests.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="p-8 text-center text-slate-500">
                          No backorder requests available
                        </td>
                      </tr>
                    ) : (
                      backorderRequests.map((request) => {
                        const firstItem = request.items?.[0] || {};
                        const itemName = firstItem.name || firstItem.item_name || request.item_name || "Unknown Item";
                        const partNumber = firstItem.part_number || request.part_number || "N/A";
                        const quantity = firstItem.quantity || request.quantity || 1;
                        
                        return (
                          <tr key={request.id} className="hover:bg-slate-50">
                            <td className="p-3">
                              <span className="font-mono text-xs text-slate-600">
                                {request.id?.slice(0, 8) || "N/A"}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-900 truncate max-w-[150px]">
                                  {request.hospital_name || "Hospital"}
                                </span>
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-sm font-medium text-slate-900 truncate max-w-[200px] block">
                                {itemName}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="text-xs text-slate-500 font-mono">
                                {partNumber}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className="text-sm font-medium text-slate-900">{quantity}</span>
                            </td>
                            <td className="p-3 text-center">
                              {request.priority === "urgent" || request.priority === "critical" ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                  <AlertTriangle className="w-3 h-3" />
                                  {request.priority?.toUpperCase()}
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                                  {request.priority || "Normal"}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                request.status === 'completed' || request.status === 'fulfilled' 
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : request.status === 'cancelled' || request.status === 'closed'
                                  ? 'bg-red-100 text-red-600'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {request.status || "Open"}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {request.has_quoted ? (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  request.quote_status === 'accepted' || request.quote_status === 'won'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : request.quote_status === 'rejected'
                                    ? 'bg-red-100 text-red-600'
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {request.quote_status === 'accepted' || request.quote_status === 'won' 
                                    ? 'Won' 
                                    : request.quote_status === 'rejected' 
                                    ? 'Lost' 
                                    : 'Pending'}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-1 text-xs text-slate-500">
                                <Calendar className="w-3 h-3" />
                                {request.created_at 
                                  ? new Date(request.created_at).toLocaleDateString() 
                                  : "N/A"}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    // Could open a detail modal here
                                    toast.info(`Request ID: ${request.id?.slice(0, 8)}`);
                                  }}
                                  data-testid={`view-request-${request.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {!request.has_quoted && (
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => openQuoteModal(request)}
                                    data-testid={`quote-request-${request.id}`}
                                  >
                                    <DollarSign className="w-4 h-4 mr-1" />
                                    Quote
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {backorderRequests.length > 0 && (
                <div className="p-3 border-t border-slate-200 bg-slate-50 text-sm text-slate-500 text-center">
                  Showing {backorderRequests.length} request{backorderRequests.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "backorders-old" && (
        <BackorderPipeline 
          viewMode="supplier" 
          supplierId={supplier?.id}
          onRefresh={fetchDashboard}
        />
      )}

      {activeTab === "orders" && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-slate-900 font-medium mb-2">Active Orders</h3>
          <p className="text-slate-500">Your active fulfillment orders will appear here.</p>
        </div>
      )}

      {activeTab === "activity" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {activityLog.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No recent activity
              </div>
            ) : (
              activityLog.map((activity, index) => (
                <div key={index} className="p-4 flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <History className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 text-sm">{activity.message || activity.action}</p>
                    <p className="text-slate-500 text-xs mt-1">
                      {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : "Recently"}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === "proposals" && (
        <div className="space-y-6" data-testid="supplier-proposals-v2">
          {/* Proposal Form */}
          {proposalForm ? (
            <div className="bg-white border border-blue-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-blue-100 bg-blue-50 flex items-center justify-between">
                <div>
                  <h3 className="text-blue-900 font-medium">Submit Proposal: {proposalForm.name}</h3>
                  <p className="text-blue-600 text-xs mt-1">{proposalForm.hospital_name} | Budget: ${(proposalForm.budget || 0).toLocaleString()}</p>
                </div>
                <button onClick={() => setProposalForm(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                {proposalForm.scope_of_work && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm text-slate-600">
                    <span className="font-medium text-slate-700">Scope: </span>{proposalForm.scope_of_work}
                  </div>
                )}
                <table className="w-full text-sm mb-4" data-testid="proposal-form-table-v2">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Phase / Scope of Work</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Your Proposed Cost</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposalItems.map((item, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <input
                            value={item.phase}
                            onChange={(e) => { const items = [...proposalItems]; items[idx] = { ...items[idx], phase: e.target.value }; setProposalItems(items); }}
                            placeholder="e.g., Plumbing Materials & Labor"
                            className="w-full border rounded px-2 py-1.5 text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          {item.included_above ? (
                            <span className="text-xs text-slate-400 italic block text-right">Included above</span>
                          ) : (
                            <input
                              type="number"
                              value={item.proposed_cost || ""}
                              onChange={(e) => { const items = [...proposalItems]; items[idx] = { ...items[idx], proposed_cost: parseFloat(e.target.value) || 0 }; setProposalItems(items); }}
                              placeholder="0"
                              className="w-full border rounded px-2 py-1.5 text-sm text-right"
                            />
                          )}
                        </td>
                        <td className="px-1 py-2">
                          <button onClick={() => setProposalItems(proposalItems.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500 p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                      <td className="px-3 py-2 font-bold text-slate-700">TOTAL</td>
                      <td className="px-3 py-2 text-right font-bold text-blue-700">
                        ${proposalItems.filter(i => !i.included_above).reduce((s, i) => s + (i.proposed_cost || 0), 0).toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                <div className="flex gap-2 mb-4">
                  <button onClick={() => setProposalItems([...proposalItems, { phase: "", proposed_cost: 0, included_above: false }])} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add Line Item
                  </button>
                  <button onClick={() => setProposalItems([...proposalItems, { phase: "", proposed_cost: 0, included_above: true }])} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add "Included Above"
                  </button>
                </div>
                <textarea value={proposalNotes} onChange={(e) => setProposalNotes(e.target.value)} placeholder="Additional notes, lead times, payment terms..." className="w-full border rounded-lg px-3 py-2 text-sm mb-4" rows={3} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setProposalForm(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                  <button onClick={submitProposal} disabled={submittingProposal || proposalItems.filter(i => i.phase).length === 0} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="submit-proposal-btn-v2">
                    {submittingProposal ? "Submitting..." : "Submit Proposal"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Available Projects */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h3 className="text-slate-900 font-medium">Available Projects</h3>
              <p className="text-slate-500 text-xs mt-1">Submit cost proposals for active projects</p>
            </div>
            {availableProjects.length === 0 ? (
              <div className="p-8 text-center">
                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No projects available for proposals</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {availableProjects.map((project) => {
                  const existing = myProposals.find(p => p.project_id === project.id);
                  return (
                    <div key={project.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div>
                        <p className="text-slate-900 font-medium text-sm">{project.name}</p>
                        <p className="text-slate-500 text-xs">{project.hospital_name} | Budget: ${(project.budget || 0).toLocaleString()}</p>
                      </div>
                      {existing ? (
                        <span className={`text-xs px-3 py-1 rounded-full ${existing.status === "submitted" ? "bg-amber-100 text-amber-700" : existing.status === "imported" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                          {existing.status === "submitted" ? "Proposal Submitted" : existing.status === "imported" ? "Accepted" : existing.status}
                        </span>
                      ) : (
                        <button onClick={() => startProposal(project)} className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700" data-testid={`propose-${project.id}`}>
                          Submit Proposal
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* My Proposals */}
          {myProposals.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-200 bg-slate-50">
                <h3 className="text-slate-900 font-medium">My Submitted Proposals ({myProposals.length})</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {myProposals.map((proposal) => (
                  <div key={proposal.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800 text-sm">{proposal.project_name}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${proposal.status === "submitted" ? "bg-amber-100 text-amber-700" : proposal.status === "imported" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{proposal.status}</span>
                        <span className="text-sm font-semibold text-blue-600">${(proposal.total_proposed || 0).toLocaleString()}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">Submitted {new Date(proposal.submitted_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "settings" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-600">Company Name</label>
              <p className="text-slate-900 font-medium">{supplier?.company_name || supplier?.name}</p>
            </div>
            <div>
              <label className="text-sm text-slate-600">Contact Name</label>
              <p className="text-slate-900">{supplier?.contact_name}</p>
            </div>
            <div>
              <label className="text-sm text-slate-600">Email</label>
              <p className="text-slate-900">{supplier?.email}</p>
            </div>
            <Button onClick={() => setShowSettings(true)} className="mt-4">
              Edit Settings
            </Button>
          </div>
        </div>
      )}

      {/* ========== INVOICES TAB ========== */}
      {activeTab === "invoices" && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="supplier-invoices-tab">
          <PortalInvoiceSubmit token={token} />
        </div>
      )}

      {/* ========== EARNINGS TAB ========== */}
      {activeTab === "earnings" && (
        <div className="space-y-6" data-testid="supplier-earnings-tab">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <DollarSign className="w-6 h-6 text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">${(dashboardData?.earnings?.total || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Total Revenue (All Time)</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <DollarSign className="w-6 h-6 text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">${(dashboardData?.earnings?.this_month || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">This Month</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <Clock className="w-6 h-6 text-amber-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">${(dashboardData?.earnings?.pending || 0).toLocaleString()}</p>
              <p className="text-sm text-slate-500">Pending Orders</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{dashboardData?.earnings?.orders_fulfilled || 0}</p>
              <p className="text-sm text-slate-500">Orders Fulfilled</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Won Quotes / Revenue History</h3>
              <p className="text-sm text-slate-500">Track your revenue through the Sterile Mate network</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Product</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Hospital</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Qty</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Unit Price</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Total</th>
                    <th className="text-left p-4 text-slate-600 font-medium text-sm">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardData?.my_quotes?.filter(q => q.status === "accepted") || []).length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-500">Win your first quote to start earning</td></tr>
                  ) : (
                    dashboardData.my_quotes.filter(q => q.status === "accepted").map((q, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-slate-900">{q.product_name || "Product"}</td>
                        <td className="p-4 text-slate-600">{q.hospital_name || "—"}</td>
                        <td className="p-4 text-slate-600">{q.available_quantity}</td>
                        <td className="p-4 text-slate-600">${q.unit_price?.toFixed(2)}</td>
                        <td className="p-4 text-emerald-600 font-bold">${(q.unit_price * q.available_quantity).toFixed(2)}</td>
                        <td className="p-4"><span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">WON</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========== CERTIFICATION TAB ========== */}
      {activeTab === "certification" && (
        <div className="space-y-6" data-testid="supplier-certification-tab">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Your Supplier Tier</h3>
            <div className="flex items-center gap-6 mb-6">
              <div className={`w-20 h-20 rounded-xl flex items-center justify-center ${
                supplier?.tier === "fortress" ? "bg-cyan-100" :
                supplier?.tier === "gold" ? "bg-amber-100" :
                supplier?.tier === "silver" ? "bg-slate-200" : "bg-amber-50"
              }`}>
                <Award className={`w-10 h-10 ${
                  supplier?.tier === "fortress" ? "text-cyan-600" :
                  supplier?.tier === "gold" ? "text-amber-500" :
                  supplier?.tier === "silver" ? "text-slate-500" : "text-amber-700"
                }`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 capitalize">{supplier?.tier || "Bronze"}</p>
                <p className="text-sm text-slate-500">{
                  supplier?.tier === "fortress" ? "Elite — Maximum visibility and premium order access" :
                  supplier?.tier === "gold" ? "Priority RFQ access and enhanced network visibility" :
                  supplier?.tier === "silver" ? "Priority alerts and expanded bid access" :
                  "Welcome to the network — fulfill orders to level up"
                }</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Supplier Tiers</h3>
            <div className="space-y-4">
              {[
                { tier: "Bronze", orders: 0, desc: "New supplier — standard RFQ access", color: "bg-amber-100 text-amber-700 border-amber-200", active: !supplier?.tier || supplier?.tier === "bronze" },
                { tier: "Silver", orders: 10, desc: "10+ fulfilled orders — priority RFQ alerts", color: "bg-slate-100 text-slate-600 border-slate-200", active: supplier?.tier === "silver" },
                { tier: "Gold", orders: 50, desc: "50+ fulfilled orders — premium rates, featured listing", color: "bg-amber-50 text-amber-600 border-amber-200", active: supplier?.tier === "gold" },
                { tier: "Fortress Partner", orders: 100, desc: "100+ orders, verified credentials — elite status, maximum visibility", color: "bg-cyan-50 text-cyan-700 border-cyan-200", active: supplier?.tier === "fortress" },
              ].map((t) => {
                const fulfilled = dashboardData?.earnings?.orders_fulfilled || 0;
                const isUnlocked = fulfilled >= t.orders;
                return (
                  <div key={t.tier} className={`flex items-center gap-4 p-4 rounded-xl border ${t.active ? t.color + " border-2" : isUnlocked ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-60"}`}>
                    <Award className={`w-6 h-6 ${t.active ? "" : isUnlocked ? "text-emerald-500" : "text-slate-300"}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-bold">{t.tier}</p>
                        <span className="text-xs text-slate-500">{t.orders === 0 ? "Entry" : `${t.orders}+ orders`}</span>
                      </div>
                      <p className="text-sm text-slate-500">{t.desc}</p>
                    </div>
                    {t.active && <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">CURRENT</span>}
                    {!t.active && isUnlocked && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Referral Section */}
          <div className="bg-gradient-to-r from-amber-50 to-cyan-50 border border-amber-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Refer & Earn</h3>
            <p className="text-sm text-slate-600 mb-4">Share your referral code with other suppliers and contractors. When they complete their first order, you both benefit.</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-3 font-mono text-sm text-slate-900">
                {supplier?.referral_code || supplier?.email?.split("@")[0]?.toUpperCase() || "YOUR-CODE"}
              </div>
              <Button variant="outline" onClick={() => {
                const code = supplier?.referral_code || supplier?.email?.split("@")[0]?.toUpperCase();
                navigator.clipboard.writeText(`https://sterilemate.com/join-network?type=supplier&ref=${code}`);
                toast.success("Referral link copied!");
              }}>Copy Link</Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <SupplierSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        supplier={supplier}
        token={token}
        onUpdate={fetchDashboard}
      />

      {/* Quote Submission Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Submit Quote
            </DialogTitle>
            <DialogDescription>
              Submit your quote for this backorder request. Your quote will be automatically sent to the hospital.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Info */}
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-500">Request from:</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {selectedRequest.hospital_name || "Hospital"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">Request ID:</span>
                  <span className="text-sm font-mono text-slate-600">{selectedRequest.id?.substring(0, 12)}...</span>
                </div>
                {selectedRequest.items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Requested Items:</p>
                    {selectedRequest.items.map((item, idx) => (
                      <p key={idx} className="text-sm text-slate-700">
                        • {item.name || item.item_name} (Qty: {item.quantity})
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Quote Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Item Name *</Label>
                  <Input
                    value={quoteForm.item_name}
                    onChange={(e) => setQuoteForm({...quoteForm, item_name: e.target.value})}
                    placeholder="e.g., Sterile Water for Irrigation"
                  />
                </div>

                <div>
                  <Label>Manufacturer Part #</Label>
                  <Input
                    value={quoteForm.manufacturer_number}
                    onChange={(e) => setQuoteForm({...quoteForm, manufacturer_number: e.target.value})}
                    placeholder="e.g., R5001-01"
                  />
                </div>

                <div>
                  <Label>Original or Substitute</Label>
                  <Select
                    value={quoteForm.original_or_substitute}
                    onValueChange={(v) => setQuoteForm({...quoteForm, original_or_substitute: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="original">Original Product</SelectItem>
                      <SelectItem value="substitute">Substitute/Equivalent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Quantity Available *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={quoteForm.quantity_available}
                    onChange={(e) => setQuoteForm({...quoteForm, quantity_available: parseInt(e.target.value) || 1})}
                  />
                </div>

                <div>
                  <Label>Unit Price ($) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={quoteForm.unit_price}
                    onChange={(e) => setQuoteForm({...quoteForm, unit_price: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <Label>Lead Time (Days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={quoteForm.lead_time_days}
                    onChange={(e) => setQuoteForm({...quoteForm, lead_time_days: parseInt(e.target.value) || 3})}
                  />
                </div>

                <div>
                  <Label>Freight</Label>
                  <Select
                    value={quoteForm.freight_included ? "included" : "extra"}
                    onValueChange={(v) => setQuoteForm({...quoteForm, freight_included: v === "included"})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="included">Freight Included</SelectItem>
                      <SelectItem value="extra">Freight Extra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label>Description / Notes</Label>
                  <Textarea
                    value={quoteForm.description}
                    onChange={(e) => setQuoteForm({...quoteForm, description: e.target.value})}
                    placeholder="Product description, specifications, or notes..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Price Preview */}
              {quoteForm.unit_price > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-emerald-700 font-medium">Your Price:</span>
                    <span className="text-xl font-bold text-emerald-700">
                      ${quoteForm.unit_price.toFixed(2)}/unit
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    Hospital will see price with standard markup applied
                  </p>
                </div>
              )}

              {/* Auto-send info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Your quote will be automatically sent to the hospital for review.
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowQuoteModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitQuote} 
                  disabled={submittingQuote || !quoteForm.item_name || !quoteForm.unit_price}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {submittingQuote ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Submit Quote
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Builder Modal */}
      <SupplierCampaignBuilder
        open={showCampaignBuilder}
        onClose={() => setShowCampaignBuilder(false)}
        supplierId={supplier?.id}
        supplierName={supplier?.company_name || supplier?.name}
        authToken={token}
        onSuccess={() => {
          fetchDashboard();
          fetchTrendingDemand();
        }}
      />

      {/* Quote Detail Modal */}
      <Dialog open={showQuoteDetailModal} onOpenChange={setShowQuoteDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Quote Details
            </DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase mb-1">Item</p>
                <p className="text-lg font-semibold text-slate-900">{selectedQuote.item_name}</p>
                <p className="text-slate-500 text-sm font-mono">#{selectedQuote.id?.substring(0, 8)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Hospital</p>
                  <p className="text-slate-900">{selectedQuote.request_hospital || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedQuote.status === "accepted" || selectedQuote.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                    selectedQuote.status === "rejected" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>
                    {selectedQuote.status?.toUpperCase() || "PENDING"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 bg-emerald-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-emerald-600 uppercase mb-1">Your Price</p>
                  <p className="text-xl font-bold text-emerald-700">${selectedQuote.unit_price?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 uppercase mb-1">Quantity</p>
                  <p className="text-xl font-bold text-emerald-700">{selectedQuote.quantity_available || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600 uppercase mb-1">Lead Time</p>
                  <p className="text-xl font-bold text-emerald-700">{selectedQuote.lead_time_days || 3} days</p>
                </div>
              </div>

              {selectedQuote.description && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Description</p>
                  <p className="text-slate-700">{selectedQuote.description}</p>
                </div>
              )}

              {selectedQuote.notes && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-1">Notes</p>
                  <p className="text-slate-700">{selectedQuote.notes}</p>
                </div>
              )}

              <div className="text-xs text-slate-500">
                Submitted: {selectedQuote.created_at ? new Date(selectedQuote.created_at).toLocaleString() : "—"}
              </div>

              <DialogFooter className="gap-2">
                {selectedQuote.status !== "approved" && selectedQuote.status !== "accepted" && (
                  <>
                    <Button variant="outline" onClick={() => { setSelectedQuote(selectedQuote); setShowDeleteConfirm(true); }} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                    <Button onClick={() => openEditQuote(selectedQuote)} className="bg-blue-600 hover:bg-blue-700">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit & Resubmit
                    </Button>
                  </>
                )}
                {(selectedQuote.status === "approved" || selectedQuote.status === "accepted") && (
                  <p className="text-sm text-amber-600 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Approved quotes cannot be modified
                  </p>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Quote Modal */}
      <Dialog open={showEditQuoteModal} onOpenChange={setShowEditQuoteModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Quote
            </DialogTitle>
            <DialogDescription>
              Update your quote details. Hospital will be notified of the changes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Item Name</Label>
              <Input 
                value={editQuoteForm.item_name} 
                onChange={e => setEditQuoteForm({...editQuoteForm, item_name: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit Price ($) *</Label>
                <Input 
                  type="number" 
                  step="0.01" 
                  min="0"
                  value={editQuoteForm.unit_price} 
                  onChange={e => setEditQuoteForm({...editQuoteForm, unit_price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Quantity Available</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={editQuoteForm.quantity_available} 
                  onChange={e => setEditQuoteForm({...editQuoteForm, quantity_available: parseInt(e.target.value) || 1})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lead Time (Days)</Label>
                <Input 
                  type="number" 
                  min="1"
                  value={editQuoteForm.lead_time_days} 
                  onChange={e => setEditQuoteForm({...editQuoteForm, lead_time_days: parseInt(e.target.value) || 3})}
                />
              </div>
              <div>
                <Label>Freight</Label>
                <Select
                  value={editQuoteForm.freight_included ? "included" : "extra"}
                  onValueChange={(v) => setEditQuoteForm({...editQuoteForm, freight_included: v === "included"})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="included">Included</SelectItem>
                    <SelectItem value="extra">Extra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={editQuoteForm.notes} 
                onChange={e => setEditQuoteForm({...editQuoteForm, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Hospital will receive email notification of this update
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditQuoteModal(false)}>Cancel</Button>
              <Button 
                onClick={handleSaveEditedQuote} 
                disabled={savingQuote || !editQuoteForm.unit_price}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {savingQuote ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Update & Notify Hospital
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Quote
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this quote? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedQuote && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-900">{selectedQuote.item_name}</p>
                <p className="text-red-700 text-sm">Price: ${selectedQuote.unit_price?.toFixed(2)}/unit</p>
                <p className="text-red-700 text-sm">Hospital: {selectedQuote.request_hospital || "N/A"}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteQuote} 
              disabled={deletingQuote}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingQuote ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SupplierLayout>
  );
}
